/*
 * ads/config.ts — configuration surface for the (Google AdSense) ads module.
 *
 * Reads the publisher identifiers from the Vite env and decides whether real ads
 * may render. Ads are enabled ONLY when a publisher client is configured AND this
 * is a production build; dev/preview builds and unconfigured environments never
 * render a live ad. This keeps the ad surface out of local dev, tests and demos
 * while letting the operator switch it on later purely by setting the env — no
 * code change required.
 */

/** Trim + coerce an optional env string to a stable, never-undefined value. */
function readEnv(value: string | undefined): string {
  return (value ?? '').trim();
}

/**
 * AdSense publisher id (data-ad-client), e.g. "ca-pub-1234567890123456". Empty
 * string when unset — treated as "ads not configured".
 */
export const ADSENSE_CLIENT: string = readEnv(import.meta.env.VITE_ADSENSE_CLIENT);

/**
 * Optional ad slot id (data-ad-slot) for the blog rail placement (square
 * rectangle in the right rail). Empty string when unset — a slot can still
 * render responsively without it.
 */
export const ADSENSE_SLOT_BLOG_RAIL: string = readEnv(import.meta.env.VITE_ADSENSE_SLOT_BLOG_RAIL);

/**
 * Optional ad slot id (data-ad-slot) for the leaderboard placement at the top of
 * the content (below the page header). Empty string when unset.
 */
export const ADSENSE_SLOT_BLOG_TOP: string = readEnv(import.meta.env.VITE_ADSENSE_SLOT_BLOG_TOP);

/**
 * Optional ad slot id (data-ad-slot) for the in-content placements interleaved
 * between article sections. Empty string when unset.
 */
export const ADSENSE_SLOT_BLOG_INCONTENT: string = readEnv(
  import.meta.env.VITE_ADSENSE_SLOT_BLOG_INCONTENT,
);

/**
 * Optional ad slot id (data-ad-slot) for the SECOND in-content placement, so the
 * two interstitials don't reuse one slot id (AdSense reporting/fill work better
 * with distinct units). Empty string when unset — callers fall back to
 * `ADSENSE_SLOT_BLOG_INCONTENT`.
 */
export const ADSENSE_SLOT_BLOG_INCONTENT_2: string = readEnv(
  import.meta.env.VITE_ADSENSE_SLOT_BLOG_INCONTENT_2,
);

/**
 * True when live AdSense ads may render: a publisher client is configured AND the
 * bundle is a production build. In dev/preview or when unconfigured nothing is
 * requested — the operator turns ads on simply by setting `VITE_ADSENSE_CLIENT`
 * in the production environment.
 */
export function isAdsEnabled(): boolean {
  if (import.meta.env.DEV && isAdsSimulated()) return true;
  return ADSENSE_CLIENT.length > 0 && import.meta.env.PROD;
}

declare global {
  interface Window {
    /** Só no bundle de dev: a suíte E2E liga o ramo "ao vivo" dos anúncios. */
    __PORTAL_E2E_ADS__?: boolean;
  }
}

/**
 * A suíte E2E roda contra o bundle de dev, onde `isAdsEnabled()` é sempre falso;
 * sem esta chave o aviso de cookies com publicidade e o carregamento do AdSense
 * condicionado ao consentimento seriam inalcançáveis. Ela é ligada por página
 * (`addInitScript`), com o script do Google interceptado pela própria suíte, e
 * some do bundle de produção junto com o ramo `DEV` acima.
 */
function isAdsSimulated(): boolean {
  return typeof window !== 'undefined' && window.__PORTAL_E2E_ADS__ === true;
}

/**
 * True when a slot that will NOT render a live ad should still paint the dashed
 * "Espaço publicitário" box. That box is layout scaffolding — it exists so whoever
 * is building the page can see where a unit would land — so it belongs to
 * dev/preview builds only. In production an ad space that is off (unconfigured
 * client, placement toggled off, signed-in reader) renders nothing at all: no
 * element, no reserved height, no empty frame in the middle of the article.
 */
export function isAdsPreviewEnabled(): boolean {
  return import.meta.env.DEV;
}
